import React, { useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const OTPInput = ({ 
  value, 
  onChange, 
  error, 
  disabled,
  onComplete 
}) => {
  const inputRefs = useRef([]);
  const otpLength = 6;

  useEffect(() => {
    if (inputRefs?.current?.[0]) {
      inputRefs?.current?.[0]?.focus();
    }
  }, []);

  useEffect(() => {
    // Only auto-verify if there's no error and OTP is complete
    if (value?.length === otpLength && onComplete && !error && !disabled) {
      // Small delay to allow user to see the complete OTP before auto-verification
      const timer = setTimeout(() => {
        onComplete(value);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [value, onComplete, error, disabled]);

  const handleChange = (index, digit) => {
    const sanitizedDigit = digit?.replace(/\D/g, '');
    if (sanitizedDigit?.length > 1) return;

    const newOTP = value?.split('');
    newOTP[index] = sanitizedDigit;
    const updatedOTP = newOTP?.join('');
    
    onChange(updatedOTP);

    if (sanitizedDigit && index < otpLength - 1) {
      inputRefs?.current?.[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e?.key === 'Backspace') {
      if (!value?.[index] && index > 0) {
        inputRefs?.current?.[index - 1]?.focus();
      }
      const newOTP = value?.split('');
      newOTP[index] = '';
      onChange(newOTP?.join(''));
    } else if (e?.key === 'ArrowLeft' && index > 0) {
      inputRefs?.current?.[index - 1]?.focus();
    } else if (e?.key === 'ArrowRight' && index < otpLength - 1) {
      inputRefs?.current?.[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e?.preventDefault();
    const pastedData = e?.clipboardData?.getData('text')?.replace(/\D/g, '')?.slice(0, otpLength);
    onChange(pastedData);
    
    const nextEmptyIndex = Math.min(pastedData?.length, otpLength - 1);
    inputRefs?.current?.[nextEmptyIndex]?.focus();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-foreground">
          Enter OTP <span className="text-error">*</span>
        </label>
        {!error && value?.length === otpLength && (
          <Icon name="ShieldCheck" size={18} color="var(--color-success)" />
        )}
      </div>
      <div className="flex gap-2 justify-center" onPaste={handlePaste}>
        {Array.from({ length: otpLength })?.map((_, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={value?.[index] || ''}
            onChange={(e) => handleChange(index, e?.target?.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            disabled={disabled}
            className={`w-12 h-14 text-center text-xl font-semibold rounded-md border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 ${
              error 
                ? 'border-error bg-error/5' 
                : value?.[index] 
                ? 'border-primary bg-primary/5' :'border-border bg-input hover:border-primary/50'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-text'}`}
            aria-label={`OTP digit ${index + 1}`}
          />
        ))}
      </div>
      {error && (
        <div className="flex items-center gap-2 text-error text-sm animate-slide-down">
          <Icon name="AlertCircle" size={16} />
          <span>{error}</span>
        </div>
      )}
      <p className="text-xs text-muted-foreground text-center">
        Enter the 6-digit code sent to your mobile number
      </p>
    </div>
  );
};

export default OTPInput;